import { useCallback, useContext, useMemo, useState } from 'react';
import {
  callGtmEvent,
  GTM_EVENT,
  LOG_ACTIONS,
  logAction,
  logPostConversionTracking
} from '@namespace/analytics';
import {
  RECAPTCHA_PLACE,
  REGISTER_COMPONENTS_TYPES,
  registerComponentsMap,
  useReCaptcha
} from '@namespace/auth';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { StepperContext } from '@namespace/common';
import {
  FORM_COMPONENTS_TYPES,
  FORM_VALIDATION_TYPES,
  FormBuilder
} from '@namespace/forms';
import { useI18n } from '@namespace/i18n';
import {
  categoryToPriorityMap,
  useNotifications
} from '@namespace/notifications';
import { useSearchParams } from '@namespace/router';
import { useLogin, useSaveUser } from '@namespace/user';
import {
  STEP_COMPONENTS_TYPES,
  stepComponentsMap
} from './components/constants';

import styles from './index.module.css';

const SecretQuestions = ({
  onGoBack,
  onGoToStep,
  classNames = {},
  isShowNeedHelp,
  ...props
}) => {
  const { formClassName = '', inputWithoutLabel = '' } = classNames;
  const { t, f, language } = useI18n();
  const DATE_FORMATS_TYPES = f.getFormats();
  const saveUser = useSaveUser();
  const { stepper } = useContext(StepperContext);
  const { formData } = stepper;

  const loadCaptchaToken = useReCaptcha(RECAPTCHA_PLACE.REGISTER);

  const {
    secretQuestionAnswer = '',
    secretQuestion = '',
    confirmAge = true,
    customInputName = ' ',
    receiveNotifications = false,
    politicalStatus = false,
    politicallyExposed = null,
    politicalDuty = null,
    sourceOfTheAssets = null,
    promo = ''
  } = formData;
  const defaultValues = {
    secretQuestionAnswer,
    secretQuestion,
    confirmAge,
    customInputName,
    receiveNotifications,
    politicalStatus,
    politicallyExposed,
    politicalDuty,
    sourceOfTheAssets,
    promo
  };
  const [politicalStatusState, setPoliticalStatus] = useState(politicalStatus);
  const { profileDocuments } = useGeneralRoutes();
  const { postConversionType } = useOtherSettings();
  const { successNotification } = useNotifications();

  const onSuccess = useCallback(() => {
    successNotification({
      message: t('web.registration.notification.success'),
      priority: categoryToPriorityMap.RESPONSE_TO_ACTION
    });
  }, [successNotification, t]);

  const { login } = useLogin({ onSuccess });

  const componentsList = [
    {
      type: FORM_COMPONENTS_TYPES.REG_SECRET_QUESTION_SELECT,
      name: 'secretQuestion',
      label: 'register.steps.secretQuestion.selectQuestion',
      placeholder: 'register.steps.secretQuestion.selectQuestionPlaceholder',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.REG_SECRET_ANSWER_INPUT,
      name: 'secretQuestionAnswer',
      placeholder: 'register.steps.secretQuestion.answer',
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED],
      classNames: {
        wrapperClassName: inputWithoutLabel
      },
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.INPUT_FIELD,
      name: 'promo',
      label: 'register.steps.welcome.promoCode',
      size: 'm'
    },
    {
      type: FORM_COMPONENTS_TYPES.CHECKBOX_FIELD,
      size: 'm',
      name: 'politicalStatus',
      label: 'register.steps.secretQuestion.politicalStatus'
    },
    {
      type: FORM_COMPONENTS_TYPES.RADIO_GROUP,
      name: 'politicallyExposed',
      label: 'register.steps.secretQuestion.exposure_type',
      direction: 'column',
      elements: [
        {
          label: 'register.steps.secretQuestion.exposure_type1',
          value: t('register.steps.secretQuestion.exposure_type1')
        },
        {
          label: 'register.steps.secretQuestion.exposure_type2',
          value: t('register.steps.secretQuestion.exposure_type2')
        },
        {
          label: 'register.steps.secretQuestion.exposure_type3',
          value: t('register.steps.secretQuestion.exposure_type3')
        }
      ],
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED]
    },
    {
      type: FORM_COMPONENTS_TYPES.RADIO_GROUP,
      name: 'politicalDuty',
      label: 'register.steps.secretQuestion.political_duty',
      direction: 'column',
      elements: [
        {
          label: 'register.steps.secretQuestion.political_duty1',
          value: t('register.steps.secretQuestion.political_duty1')
        },
        {
          label: 'register.steps.secretQuestion.political_duty2',
          value: t('register.steps.secretQuestion.political_duty2')
        },
        {
          label: 'register.steps.secretQuestion.political_duty3',
          value: t('register.steps.secretQuestion.political_duty3')
        },
        {
          label: 'register.steps.secretQuestion.political_duty4',
          value: t('register.steps.secretQuestion.political_duty4')
        },
        {
          label: 'register.steps.secretQuestion.political_duty5',
          value: t('register.steps.secretQuestion.political_duty5')
        },
        {
          label: 'register.steps.secretQuestion.political_duty6',
          value: t('register.steps.secretQuestion.political_duty6')
        },
        {
          label: 'register.steps.secretQuestion.political_duty7',
          value: t('register.steps.secretQuestion.political_duty7')
        },
        {
          label: 'register.steps.secretQuestion.political_duty8',
          value: t('register.steps.secretQuestion.political_duty8')
        },
        {
          label: 'register.steps.secretQuestion.political_duty9',
          value: t('register.steps.secretQuestion.political_duty9')
        },
        {
          label: 'register.steps.secretQuestion.political_duty10',
          value: t('register.steps.secretQuestion.political_duty10')
        },
        {
          label: 'register.steps.secretQuestion.political_duty11',
          value: t('register.steps.secretQuestion.political_duty11')
        },
        {
          label: 'register.steps.secretQuestion.political_duty12',
          value: t('register.steps.secretQuestion.political_duty12')
        }
      ],
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED]
    },
    {
      type: STEP_COMPONENTS_TYPES.SOURCE_OF_THE_ASSETS_FIELD,
      name: 'sourceOfTheAssets',
      label: 'register.steps.secretQuestion.source_of_assets',
      elements: [
        {
          label: 'register.steps.secretQuestion.source_of_assets1',
          value: t('register.steps.secretQuestion.source_of_assets1')
        },
        {
          label: 'register.steps.secretQuestion.source_of_assets2',
          value: t('register.steps.secretQuestion.source_of_assets2')
        },
        {
          label: 'register.steps.secretQuestion.source_of_assets3',
          value: t('register.steps.secretQuestion.source_of_assets3')
        },
        {
          label: 'register.steps.secretQuestion.source_of_assets4',
          value: t('register.steps.secretQuestion.source_of_assets4')
        },
        {
          label: 'register.steps.secretQuestion.source_of_assets5',
          value: t('register.steps.secretQuestion.source_of_assets5')
        },
        {
          label: 'register.steps.secretQuestion.source_of_assets6',
          value: t('register.steps.secretQuestion.source_of_assets6')
        },
        {
          label: 'register.steps.secretQuestion.source_of_assets7',
          value: t('register.steps.secretQuestion.source_of_assets7')
        }
      ],
      validationTypes: [FORM_VALIDATION_TYPES.REQUIRED]
    },
    {
      type: STEP_COMPONENTS_TYPES.CONFIRM_AGE_FIELD,
      name: 'confirmAge',
      size: 'm',
      validationTypes: [
        {
          type: FORM_VALIDATION_TYPES.REQUIRED,
          parameters: ['validators.requiredValidator.confirmAge']
        }
      ]
    },
    {
      type: FORM_COMPONENTS_TYPES.CHECKBOX_FIELD,
      size: 'm',
      name: 'receiveNotifications',
      label: 'register.steps.secretQuestion.receiveNotifications'
    },
    {
      id: 'submit',
      type: FORM_COMPONENTS_TYPES.SUBMIT_BUTTON,
      value: 'register.steps.secretQuestion.register',
      size: 'xl'
    },
    {
      type: REGISTER_COMPONENTS_TYPES.BACK_BUTTON,
      onGoBack
    }
  ];

  if (isShowNeedHelp) {
    componentsList.push({
      type: FORM_COMPONENTS_TYPES.NEED_HELP,
      needHelpText: 'web.register.needhelp.link',
      dataRole: 'register-need-help'
    });
  }

  const extendedComponentsList = {
    politicalStatus: {
      onChange: setPoliticalStatus
    },
    politicallyExposed: {
      classNames: {
        wrapperClassName: styles.controlWrapper,
        groupLabelClassName: styles.radioGroupLabel,
        groupClassName: styles.radioGroup,
        inputClassName: styles.radioClassName,
        labelTextClassName: styles.radioLabelText,
        className: styles.radioWrapper
      }
    },
    politicalDuty: {
      classNames: {
        wrapperClassName: styles.controlWrapper,
        groupLabelClassName: styles.radioGroupLabel,
        groupClassName: styles.radioGroup,
        inputClassName: styles.radioClassName,
        labelTextClassName: styles.radioLabelText,
        className: styles.radioWrapper
      }
    },
    sourceOfTheAssets: {
      classNames: {
        wrapperClassName: styles.controlWrapper,
        groupLabelClassName: styles.radioGroupLabel,
        groupClassName: styles.radioGroup,
        inputClassName: styles.radioClassName,
        labelTextClassName: styles.radioLabelText,
        className: styles.radioWrapper,
        customInputClassName: styles.customInputClassName
      }
    }
  };

  const mergedComponentsList = useMemo(
    () =>
      componentsList.map((component) => ({
        ...component,
        ...extendedComponentsList[component.name || component.id]
      })),
    []
  );

  const modifiedComponentsList = useMemo(() => {
    if (!politicalStatusState) {
      const politicalStatusFields = [
        'sourceOfTheAssets',
        'politicalDuty',
        'politicallyExposed'
      ];
      return mergedComponentsList.filter(
        (component) => !politicalStatusFields.includes(component.name)
      );
    }

    return mergedComponentsList;
  }, [mergedComponentsList, politicalStatusState]);

  const convertToServer = useCallback(
    (date) =>
      f
        .getDateTime({
          date,
          format: DATE_FORMATS_TYPES.CLIENT_API,
          fromUTC: true
        })
        .toFormat(DATE_FORMATS_TYPES.API),
    [f, DATE_FORMATS_TYPES]
  );
  const { from: locationFrom } = useSearchParams();
  const submit = useCallback(
    async (fields) => {
      const reCaptchaData = await loadCaptchaToken();
      const formFields = {
        ...formData,
        ...fields
      };

      await saveUser({
        ...formFields,
        phone: formFields.phoneNumber,
        birthdate: convertToServer(formFields.dateOfBirth),
        documentDate: convertToServer(formFields.documentDate),
        language,
        reCaptchaData
      })
        .then((data) => {
          if (data?.response) {
            login({
              username: formData.email,
              password: formData.password,
              nextPage: profileDocuments,
              withAutoLogin: false
            });
            callGtmEvent(GTM_EVENT.REGISTRATION_CR, {
              event_category: 'Conversions',
              event_label: GTM_EVENT.REGISTRATION_CR
            });
            logAction(LOG_ACTIONS.REGISTRATION_SUCCESS, {
              country: formFields.country,
              userId: data.response.id,
              location: locationFrom || '',
              language
            });
            logPostConversionTracking(
              LOG_ACTIONS.POST_REGISTRATION,
              postConversionType
            );
          }
        })
        .catch((error) => {
          logAction(LOG_ACTIONS.REGISTRATION_FAILED, { error });
          onGoToStep(formFields, 2, error);
        });
    },
    [
      saveUser,
      convertToServer,
      formData,
      language,
      locationFrom,
      login,
      onGoToStep,
      profileDocuments,
      loadCaptchaToken
    ]
  );

  return (
    <FormBuilder
      wrapperClassName={formClassName}
      componentsMap={{ ...registerComponentsMap, ...stepComponentsMap }}
      components={modifiedComponentsList}
      formConfig={{ defaultValues }}
      onSubmit={submit}
      formProps={{
        ...props
      }}
    />
  );
};

export default SecretQuestions;
